import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "react-feather";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import { useGetPettyCashLedgerQuery, useGetPettyCashFundsQuery } from "../../pettyCashApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import {
  groupByFundAndDay, fundPeriodLine, buildLedgerExport, LEDGER_EXPORT_COLUMNS,
  LEDGER_SORTS, applyLedgerSort, isFlatSort,
} from "../ledgerGrouping";
import { useAllPages } from "../../useAllPages";
import LedgerTable from "../components/LedgerTable";
import LedgerPrintModal from "../components/LedgerPrintModal";

// Every page of the range is loaded (useAllPages) - the API serves at most 200
// entries a request, and showing only the first request lost the oldest days
// of a long range, in the export/print too (2026-09-11).
const ledgerIdOf = (r) => r.ledgerId;

const printButtonStyle = {
  backgroundColor: "#6E1423",
  borderColor: "#6E1423",
  color: "#fff",
  padding: "0.5rem 1rem",
  fontWeight: 500,
};

// Read-only, no per-record workflow. Accepts ?fundId= so Fund Detail's
// "View Ledger" link lands here pre-filtered to one fund.
const LedgerPage = () => {
  const [searchParams] = useSearchParams();
  const initialFundId = searchParams.get("fundId") || "";
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [fundId, setFundId] = useState(initialFundId);
  // Sort (2026-09-22): date keeps the day book; voucher / receipt number list the
  // entries flat in that order. Applied on the screen, the export and the print.
  const [sortBy, setSortBy] = useState("DATE_DESC");
  const flat = isFlatSort(sortBy);
  const sortLabel = LEDGER_SORTS.find((s) => s.value === sortBy)?.label;
  const [showPrint, setShowPrint] = useState(false);

  useEffect(() => {
    if (initialFundId) setFundId(initialFundId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFundId]);

  // Deliberately unfiltered (unlike Fund Balances' own active-only default)
  // - a deactivated fund can still have real ledger history worth viewing.
  // Only the header branch's funds (2026-10-02); a picked fund that is not in the new
  // branch is dropped so the ledger does not go silently empty.
  const { data: funds = [], isSuccess: fundsLoaded } = useGetPettyCashFundsQuery({ branchId: selectedBranchId ?? undefined });
  useEffect(() => {
    if (fundsLoaded && fundId && !funds.some((f) => String(f.fundId) === String(fundId))) setFundId("");
  }, [fundsLoaded, funds, fundId]);

  const baseParams = useMemo(
    () => ({
      branchId: selectedBranchId ?? undefined,
      fundId: fundId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [selectedBranchId, fundId, dateFrom, dateTo]
  );
  const {
    items: allItems,
    totalCount,
    isLoading,
    isFetching,
    isComplete,
    isCapped,
    cap,
    loaders,
  } = useAllPages(useGetPettyCashLedgerQuery, baseParams, ledgerIdOf);

  const fundGroups = useMemo(() => applyLedgerSort(groupByFundAndDay(allItems), sortBy), [allItems, sortBy]);

  const selectedFund = funds.find((f) => String(f.fundId) === String(fundId));
  const exportBranchCode =
    selectedFund?.branchCode || availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const { exportRows, groupOrder } = useMemo(() => buildLedgerExport(fundGroups, sortBy), [fundGroups, sortBy]);
  const exportSubtitle = [
    `Branch: ${exportBranchCode || "All"}`,
    `Fund: ${selectedFund ? selectedFund.fundName : "All funds"}`,
    `Period: ${dateFrom} to ${dateTo}`,
    `Sorted by: ${sortLabel}`,
    ...fundGroups.map(fundPeriodLine),
  ];
  // While the filters' new data is still arriving the rows on hand can be the
  // previous filter's - never print or export those under the new heading.
  const canOutput = isComplete && !isFetching && fundGroups.length > 0;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Petty Cash Ledger</h4>
              <h6>
                {flat
                  ? `Every transaction in ${sortLabel?.toLowerCase()} order - the balance column is the fund's balance after that entry`
                  : "Every transaction, grouped by day - each day's opening balance carries over from the previous day's closing figure"}
              </h6>
            </div>
          </div>
        </div>

        {loaders}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2 mb-3">
              <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <select className="form-control form-control-sm" style={{ width: 220 }} value={fundId} onChange={(e) => setFundId(e.target.value)}>
                    <option value="">All Funds</option>
                    {funds.map((f) => (
                      <option key={f.fundId} value={f.fundId}>{f.fundName} ({f.branchCode})</option>
                    ))}
                  </select>
                  <label className="small text-muted mb-0">Sort</label>
                  <select className="form-control form-control-sm" style={{ width: 190 }} value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort ledger by">
                    {LEDGER_SORTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {!isComplete && !isLoading && (
                    <small className="text-muted">Loading all {totalCount} entries...</small>
                  )}
                  <button
                    type="button"
                    className="btn d-inline-flex align-items-center"
                    style={printButtonStyle}
                    disabled={!canOutput}
                    onClick={() => setShowPrint(true)}
                  >
                    <Printer size={16} className="me-1" />
                    Print
                  </button>
                  {canOutput ? (
                    <ListExportToolbar
                      title="Petty Cash Ledger"
                      subtitleLines={exportSubtitle}
                      branchCode={exportBranchCode}
                      columns={LEDGER_EXPORT_COLUMNS}
                      rows={exportRows}
                      groupBy={(x) => x.group}
                      groupOrder={groupOrder}
                      fileName={`PettyCashLedger_${dateFrom}_to_${dateTo}`}
                    />
                  ) : null}
                </div>
              </div>
            </div>

            {isCapped && (
              <div className="alert alert-warning py-2 px-3 mb-2">
                This range has {totalCount} entries; only the newest {cap} are shown. Choose a shorter period.
              </div>
            )}

            {isLoading && <p className="text-muted">Loading...</p>}
            {!isLoading && fundGroups.length === 0 && <p className="text-muted">No ledger entries match these filters.</p>}

            <LedgerTable fundGroups={fundGroups} flat={flat} />
          </div>
        </div>
      </div>

      {showPrint && (
        <LedgerPrintModal
          show={showPrint}
          onClose={() => setShowPrint(false)}
          fundGroups={fundGroups}
          dateFrom={dateFrom}
          dateTo={dateTo}
          flat={flat}
          orderLabel={flat ? sortLabel : undefined}
        />
      )}
    </div>
  );
};

export default LedgerPage;
